import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { fmtDate, initials } from '../lib/dates';
import type { ExamForecast, ExamForecastsResponse } from '../types';

// ─── Prévisions d'examen ─────────────────────────────────────────────────────
// Le moniteur indique la quinzaine où son élève sera prêt pour l'examen pratique ; Permio en fait
// la demande de réservation automatique (centres favoris de l'école), ou dit ce qui manque.

const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const monthLabel = (month: string) => {
  const label = MONTH_FORMAT.format(new Date(`${month}-15T12:00:00Z`));
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const STATUS: Record<
  ExamForecast['status'],
  { label: string; className: string; Icon: typeof Clock3 }
> = {
  pending: {
    label: 'Transmise à Permio',
    className: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    Icon: Sparkles,
  },
  requested: {
    label: 'Demande de réservation créée',
    className: 'bg-green-50 text-green-700 border-green-200',
    Icon: CheckCircle2,
  },
  blocked: {
    label: 'À compléter par le secrétariat',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
    Icon: AlertTriangle,
  },
  expired: {
    label: 'Quinzaine passée',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
    Icon: Clock3,
  },
};

const errorMessage = (error: unknown, fallback: string) =>
  (isAxiosError(error) &&
    (error.response?.data as { message?: string | string[] } | undefined)?.message?.toString()) ||
  fallback;

function ForecastCard({
  forecast,
  onEdit,
  onRemove,
  removing,
}: {
  forecast: ExamForecast;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  const status = STATUS[forecast.status];
  const [first = '', ...rest] = forecast.candidateName.split(' ');
  const [confirming, setConfirming] = useState(false);
  return (
    <li
      className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
      aria-label={`Prévision de ${forecast.candidateName}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {initials(first, rest.join(' ') || first)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{forecast.candidateName}</div>
          <div className="text-sm text-slate-700 mt-0.5">
            Prêt·e pour l'examen : <span className="font-semibold">{forecast.label}</span>
          </div>
          <div className="text-xs text-slate-500">{forecast.range}</div>
          <span
            className={`mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${status.className}`}
          >
            <status.Icon size={12} aria-hidden />
            {status.label}
            {forecast.status === 'requested' && forecast.handledAt
              ? ` le ${fmtDate(forecast.handledAt)}`
              : ''}
          </span>
          {forecast.status === 'pending' && (
            <p className="mt-2 text-xs text-slate-500">
              Permio prépare la demande de réservation sur cette quinzaine…
            </p>
          )}
          {forecast.status === 'blocked' && (
            <p className="mt-2 text-xs text-amber-800">
              {forecast.reasonLabel ?? 'La demande n’a pas pu être créée.'} Le secrétariat est
              prévenu ; Permio crée la demande dès que c’est corrigé.
            </p>
          )}
          {forecast.status === 'requested' && (
            <p className="mt-2 text-xs text-slate-500">
              Le secrétariat la suit dans « Réservation auto » et peut l’ajuster.
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="px-3 py-1.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5"
        >
          <Pencil size={14} aria-hidden />
          Modifier
        </button>
        {confirming ? (
          <>
            <span className="text-xs text-slate-600">
              {forecast.requestCreated
                ? 'La demande déjà créée restera au secrétariat.'
                : 'Retirer cette prévision ?'}
            </span>
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
            >
              {removing ? 'Retrait…' : 'Retirer'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              Annuler
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="px-3 py-1.5 text-slate-500 rounded-lg hover:bg-red-50 hover:text-red-700 text-sm font-medium flex items-center gap-1.5"
          >
            <Trash2 size={14} aria-hidden />
            Retirer la prévision
          </button>
        )}
      </div>
    </li>
  );
}

export default function ForecastsView() {
  const queryClient = useQueryClient();
  const students = useQuery({ queryKey: ['students'], queryFn: instructorApi.getStudents });
  const forecasts = useQuery({
    queryKey: ['exam-forecasts'],
    queryFn: instructorApi.getExamForecasts,
    // Tant que Permio prépare une demande, l'écran suit son avancement.
    refetchInterval: (query) =>
      query.state.data?.items.some((f) => f.status === 'pending') ? 4_000 : 60_000,
  });
  const periods = forecasts.data?.periods ?? [];
  const months = useMemo(() => [...new Set(periods.map((p) => p.month))], [periods]);

  const [candidateId, setCandidateId] = useState('');
  const [month, setMonth] = useState('');
  const [half, setHalf] = useState<1 | 2 | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!month && months.length) setMonth(months[0]);
  }, [month, months]);
  const available = (h: 1 | 2) => periods.find((p) => p.month === month && p.half === h);
  useEffect(() => {
    if (half && !available(half)) setHalf(null);
  }, [month, periods]);

  const current = forecasts.data?.items.find((f) => f.candidateId === candidateId);
  const updated = (data: ExamForecastsResponse) =>
    queryClient.setQueryData(['exam-forecasts'], data);

  const save = useMutation({
    mutationFn: () => instructorApi.setExamForecast(candidateId, month, half!),
    onMutate: () => {
      setError('');
      setNotice('');
    },
    onSuccess: (data) => {
      updated(data);
      const name = data.items.find((f) => f.candidateId === candidateId)?.candidateName ?? 'l’élève';
      setNotice(
        `Prévision enregistrée pour ${name} : Permio prépare la demande de réservation automatique.`,
      );
      setCandidateId('');
      setHalf(null);
    },
    onError: (e) => setError(errorMessage(e, 'La prévision n’a pas pu être enregistrée.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => instructorApi.clearExamForecast(id),
    onSuccess: (data) => {
      updated(data);
      setNotice('Prévision retirée.');
    },
    onError: (e) => setError(errorMessage(e, 'La prévision n’a pas pu être retirée.')),
  });

  const edit = (f: ExamForecast) => {
    setCandidateId(f.candidateId);
    if (periods.some((p) => p.month === f.month)) {
      setMonth(f.month);
      setHalf(f.half);
    }
    setNotice('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const items = forecasts.data?.items ?? [];
  const canSave = !!candidateId && !!month && !!half && !save.isPending;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-slate-900">Prévisions d'examen</h1>
        <p className="text-slate-600">
          Indiquez la quinzaine où chaque élève sera prêt pour l'examen pratique. Permio crée alors
          la demande de réservation automatique sur cette période, avec les centres d'examen
          favoris de l'école.
        </p>
      </div>

      <form
        className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-5"
        aria-label="Nouvelle prévision d'examen"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <CalendarClock size={18} className="text-indigo-600" aria-hidden />
          Nouvelle prévision
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">Élève</span>
            <select
              value={candidateId}
              onChange={(e) => setCandidateId(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-50"
            >
              <option value="">Choisir un élève</option>
              {(students.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">Mois</span>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-50"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <fieldset>
          <legend className="block text-sm font-medium text-slate-700 mb-1.5">Quinzaine</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {([1, 2] as const).map((h) => {
              const period = available(h);
              const on = half === h;
              return (
                <label
                  key={h}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${
                    !period
                      ? 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : on
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-900 cursor-pointer'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50 cursor-pointer'
                  }`}
                >
                  <input
                    type="radio"
                    name="half"
                    className="accent-indigo-600"
                    checked={on}
                    disabled={!period}
                    onChange={() => setHalf(h)}
                  />
                  <span>
                    <span className="block font-semibold">
                      {h === 1 ? '1re quinzaine' : '2e quinzaine'}
                    </span>
                    <span className="block text-xs">
                      {period ? period.range : 'Déjà passée'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {current && (
          <p className="text-xs text-slate-500">
            Remplace la prévision actuelle de cet élève ({current.label}).
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={!canSave}
          className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {save.isPending && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Enregistrer la prévision
        </button>
      </form>

      {notice && (
        <p
          role="status"
          className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900"
        >
          {notice}
        </p>
      )}

      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Prévisions de vos élèves</h2>
        {forecasts.isLoading ? (
          <p className="text-slate-500">Chargement des prévisions…</p>
        ) : forecasts.isError ? (
          <p className="text-red-600">Impossible de charger les prévisions.</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            Aucune prévision pour le moment.
          </div>
        ) : (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map((f) => (
              <ForecastCard
                key={f.candidateId}
                forecast={f}
                onEdit={() => edit(f)}
                onRemove={() => remove.mutate(f.candidateId)}
                removing={remove.isPending && remove.variables === f.candidateId}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
